import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse } from '@/types';
import { STORAGE_KEYS } from '@/config/constants';
import queryString from 'query-string';
import { CustomMap } from '@/types/ICustomMap';
import { routes } from '@/config/routes';
/**
 * Configure axios for API requests
 * In production, the frontend and backend are served from the same domain
 * In development, we use the proxy configured in vite.config.ts
 */

const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Public, token-authenticated page prefixes (proposal / work-order / pay links).
 * Pages under these paths are reachable by recipients who may carry a stale or
 * expired session from a *different* account. They must never be subjected to the
 * shared session-expiry machinery (token refresh + hard redirect to login), and
 * any background authenticated request should be suppressed while on them.
 */
const PUBLIC_TOKEN_PATH_PREFIXES = ['/proposal/', '/work-order/', '/pay/', '/t/'];

export const isPublicTokenPath = (pathname: string | undefined | null): boolean =>
    !!pathname && (pathname === '/feedback' || pathname === '/feedback/' || PUBLIC_TOKEN_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix)));

export const api = axios.create({
    baseURL: apiUrl,
    headers: {
        'Content-Type': 'application/json',
    },
    paramsSerializer: {
        serialize: params => {
            return queryString.stringify(params, { skipEmptyString: true, skipNull: true });
        },
    },
});

/**
 * Token-less axios client for PUBLIC, token-authenticated endpoints such as
 * `/public/proposals/:token` and `/public/work-orders/:token`.
 *
 * These pages are opened by recipients (customers) who may ALSO have a stale or
 * expired partner / end-customer session lingering in localStorage from a prior
 * order. The shared `api` instance below attaches that bearer token on every
 * request and, on a 401 from any incidental authenticated request, attempts a
 * token refresh and HARD-redirects the browser to a login page — which silently
 * knocks the recipient off the public proposal/work-order page (while incognito,
 * having no stored session, works fine).
 *
 * `publicApi` deliberately carries NO Authorization header and NO refresh/redirect
 * response interceptor, so public token pages are fully decoupled from whatever
 * session state happens to exist in the browser.
 */
export const publicApi = axios.create({
    baseURL: apiUrl,
    headers: {
        'Content-Type': 'application/json',
    },
    paramsSerializer: {
        serialize: params => {
            return queryString.stringify(params, { skipEmptyString: true, skipNull: true });
        },
    },
});

let isRefreshingToken = false;
let failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: CustomMap | null) => void;
    config: AxiosRequestConfig;
}> = [];

const processQueue = (error: CustomMap | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(api(prom.config));
        }
    });
    failedQueue = [];
};

const getLoginRedirectPath = () =>
    window?.location?.pathname?.startsWith('/ec') ? routes.ec.login : routes.auth.login;

// Add authorization token to requests if available
api.interceptors.request.use((config) => {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle token expiration and other response errors
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Never run session-expiry handling while on a public token page. These
        // pages (proposal/work-order/pay links) can be opened with a leftover
        // session from another account; refreshing or hard-redirecting here would
        // knock the recipient off the public page. Let the caller handle the error.
        if (error.response?.status === 401 && isPublicTokenPath(window?.location?.pathname)) {
            return Promise.reject(error);
        }

        // If unauthorized and we have a refresh token, try to refresh
        if (error.response?.status === 401 && localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)) {
            if (isRefreshingToken) {
                // If another refresh is already in progress, add request to queue
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject, config: originalRequest });
                });
            }

            isRefreshingToken = true;
            try {
                console.log('Attempting to refresh token...');

                // Call refresh token endpoint
                const refreshResponse = await axios.post(`${apiUrl}/auth/refresh`, {
                    refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
                });

                console.log('Refresh token response:', refreshResponse.data);

                // Handle different response structures
                let accessToken, refreshToken;

                if (refreshResponse.data.data) {
                    // Nested data structure
                    accessToken = refreshResponse.data.data.accessToken;
                    refreshToken = refreshResponse.data.data.refreshToken;
                } else {
                    // Direct response
                    accessToken = refreshResponse.data.accessToken;
                    refreshToken = refreshResponse.data.refreshToken;
                }

                // Check if we got valid tokens
                if (accessToken && refreshToken) {
                    console.log('Token refresh successful, updating tokens');

                    // Update tokens in localStorage
                    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
                    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);

                    // Update the authorization header
                    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

                    // Process all queued requests
                    processQueue();

                    // Retry the original request
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                    return axios(originalRequest);
                } else {
                    console.error('Invalid token refresh response format:', refreshResponse.data);
                    processQueue(new Error('Invalid token refresh response'));
                    throw new Error('Invalid token refresh response');
                }
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                // If refresh fails, log out user
                localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
                localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
                localStorage.removeItem(STORAGE_KEYS.USER);
                localStorage.removeItem(STORAGE_KEYS.IMPERSONATED_ACCOUNT);

                // Process queue with error
                processQueue(refreshError as CustomMap | null);

                window.location.href = getLoginRedirectPath();
            } finally {
                isRefreshingToken = false;
            }
        } else if (!localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) && error.response?.status === 401) {
            localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.USER);
            localStorage.removeItem(STORAGE_KEYS.IMPERSONATED_ACCOUNT);
            window.location.href = getLoginRedirectPath();
        }

        return Promise.reject(error);
    }
);

// Helper to handle API errors
// const handleApiError = (error: any) => {
//   console.error('API error:', error);
//   if (axios.isAxiosError(error)) {
//     console.error('Axios error details:', {
//       status: error.response?.status,
//       statusText: error.response?.statusText,
//       data: error.response?.data,
//       message: error.message
//     });
//   }
// };

// Generic GET request
export const fetchData = async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    try {
        const response: AxiosResponse<ApiResponse<T>> = await api.get(url, config);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return error.response.data as ApiResponse<T>;
        }
        return {
            success: false,
            message: 'Network error occurred',
        };
    }
};

// Generic POST request
export const postData = async <T>(url: string, data: CustomMap, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    console.log(`Making POST request to: /api${url}`, data);
    try {
        const response: AxiosResponse<ApiResponse<T>> = await api.post(url, data, config);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return error.response.data as ApiResponse<T>;
        }
        return {
            success: false,
            message: 'Network error occurred',
        };
    }
};

// Generic PATCH request
export const patchData = async <T>(url: string, data: CustomMap, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    try {
        const response: AxiosResponse<ApiResponse<T>> = await api.patch(url, data, config);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return error.response.data as ApiResponse<T>;
        }
        return {
            success: false,
            message: 'Network error occurred',
        };
    }
};

// Generic PUT request
export const putData = async <T>(url: string, data: CustomMap, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    console.log(`Making PUT request to: /api${url}`, data);
    try {
        const response: AxiosResponse<ApiResponse<T>> = await api.put(url, data, config);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return error.response.data as ApiResponse<T>;
        }
        return {
            success: false,
            message: 'Network error occurred',
        };
    }
};

// Generic DELETE request
export const deleteData = async <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    try {
        const response: AxiosResponse<ApiResponse<T>> = await api.delete(url, config);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return error.response.data as ApiResponse<T>;
        }
        return {
            success: false,
            message: 'Network error occurred',
        };
    }
};

export default api;
